// Pure progress + visibility helpers shared between the API Worker and the
// web app. Operates on minimal structural types so each caller can pass its
// own (DB row vs frontend) shapes without conversion.

export type ProgressAdoptionTrigger = {
  trigger_value: string;
  document_id: string;
};

export type ProgressQuestion = {
  id: string;
  type: string;
  required: boolean;
  requires_upload: boolean;
  parent_id: string | null;
  condition: { value: unknown } | null;
  adoptable_doc_id: string | null;
  adoption_triggers: ProgressAdoptionTrigger[];
};

export type ProgressSubmission = {
  question_id: string;
  answer: unknown;
  submission_files: { id: string; option_key?: string | null }[];
  submission_adoptions: { document_id: string; option_key?: string | null }[];
};

export type CompletionReason = 'no_answer' | 'upload_required' | 'adoption_required';

export type ProgressResult = {
  progress: number;
  totalRequired: number;
  completedRequired: number;
  visibleIds: Set<string>;
  missing: Array<{ question_id: string; reason: CompletionReason }>;
};

export function matchesCondition(answer: unknown, expected: unknown): boolean {
  if (Array.isArray(expected)) {
    if (!Array.isArray(answer)) return false;
    return expected.every((v) => (answer as unknown[]).includes(v));
  }
  if (Array.isArray(answer)) return (answer as unknown[]).includes(expected);
  return answer === expected;
}

export function isVisible(
  q: ProgressQuestion,
  byId: Map<string, ProgressQuestion>,
  subByQ: Map<string, ProgressSubmission>,
): boolean {
  if (!q.parent_id) return true;
  const parent = byId.get(q.parent_id);
  if (!parent) return false;
  if (!isVisible(parent, byId, subByQ)) return false;
  const parentSub = subByQ.get(parent.id);
  if (!parentSub) return false;
  if (!q.condition) return true;
  return matchesCondition(parentSub.answer, q.condition.value);
}

export function visibleQuestionSet(
  questions: ProgressQuestion[],
  subByQ: Map<string, ProgressSubmission>,
): Set<string> {
  const byId = new Map<string, ProgressQuestion>();
  for (const q of questions) byId.set(q.id, q);
  const visible = new Set<string>();
  for (const q of questions) {
    if (isVisible(q, byId, subByQ)) visible.add(q.id);
  }
  return visible;
}

export function isQuestionComplete(
  q: ProgressQuestion,
  sub: ProgressSubmission | undefined,
): CompletionReason | null {
  if (!sub) return 'no_answer';

  if (q.type === 'document_adopt') {
    if (!q.adoptable_doc_id) return null;
    const has = sub.submission_adoptions.some((a) => a.document_id === q.adoptable_doc_id);
    return has ? null : 'adoption_required';
  }

  if (q.type === 'document_upload') {
    // A document_upload question is satisfied by either an uploaded file or an
    // adoption row for the linked standard document (when one is configured).
    if (q.adoptable_doc_id) {
      const has = sub.submission_adoptions.some((a) => a.document_id === q.adoptable_doc_id);
      if (has) return null;
    }
    return sub.submission_files.length > 0 ? null : 'upload_required';
  }

  if (q.type === 'signature') {
    if (!sub.answer || typeof sub.answer !== 'object' || Array.isArray(sub.answer)) {
      return 'no_answer';
    }
    const sig = sub.answer as {
      signature_data_url?: unknown;
      signer_name?: unknown;
      signed_at?: unknown;
    };
    if (
      typeof sig.signature_data_url !== 'string' ||
      sig.signature_data_url.length === 0 ||
      typeof sig.signer_name !== 'string' ||
      sig.signer_name.trim() === '' ||
      typeof sig.signed_at !== 'string' ||
      sig.signed_at.length === 0
    ) {
      return 'no_answer';
    }
    return null;
  }

  if (q.type === 'multiselect_uploads') {
    if (sub.answer === null || sub.answer === undefined) return 'no_answer';
    if (!Array.isArray(sub.answer) || sub.answer.length === 0) return 'no_answer';
    const triggers = new Map(q.adoption_triggers.map((t) => [t.trigger_value, t.document_id]));
    for (const opt of sub.answer as unknown[]) {
      const key = String(opt);
      const adoptableDoc = triggers.get(key);
      const adopted =
        adoptableDoc !== undefined &&
        sub.submission_adoptions.some(
          (a) => a.document_id === adoptableDoc && (a.option_key ?? null) === key,
        );
      if (adopted) continue;
      const hasFile = sub.submission_files.some((f) => (f.option_key ?? null) === key);
      if (!hasFile) return 'upload_required';
    }
    return null;
  }

  if (sub.answer === null || sub.answer === undefined) return 'no_answer';
  if (typeof sub.answer === 'string' && sub.answer.trim() === '') return 'no_answer';
  if (Array.isArray(sub.answer) && sub.answer.length === 0) return 'no_answer';

  if (q.requires_upload && sub.submission_files.length === 0) return 'upload_required';

  for (const trigger of q.adoption_triggers) {
    if (matchesCondition(sub.answer, trigger.trigger_value)) {
      const has = sub.submission_adoptions.some((a) => a.document_id === trigger.document_id);
      if (!has) return 'adoption_required';
    }
  }
  return null;
}

// ----------------------------------------------------------------------
// Company information completeness
// ----------------------------------------------------------------------

// Fields the company must populate before they can submit for review.
// trustee_info is omitted because it is only required "if applicable".
export const REQUIRED_COMPANY_INFO_FIELDS = [
  'abn',
  'primary_contact_name',
  'contact_role',
  'contact_phone',
  'contact_email',
  'employee_count',
] as const;

export type RequiredCompanyInfoField = (typeof REQUIRED_COMPANY_INFO_FIELDS)[number];

export type CompanyInfoForGate = Partial<Record<RequiredCompanyInfoField, unknown>>;

export function missingCompanyInfoFields(
  info: CompanyInfoForGate,
): RequiredCompanyInfoField[] {
  const missing: RequiredCompanyInfoField[] = [];
  for (const field of REQUIRED_COMPANY_INFO_FIELDS) {
    const value = info[field];
    if (value === null || value === undefined) {
      missing.push(field);
      continue;
    }
    if (typeof value === 'string' && value.trim() === '') missing.push(field);
  }
  return missing;
}

export function computeProgress(
  questions: ProgressQuestion[],
  submissions: ProgressSubmission[],
): ProgressResult {
  const subByQ = new Map<string, ProgressSubmission>();
  for (const s of submissions) subByQ.set(s.question_id, s);

  const visibleIds = visibleQuestionSet(questions, subByQ);

  let totalRequired = 0;
  let completedRequired = 0;
  const missing: ProgressResult['missing'] = [];

  for (const q of questions) {
    if (!visibleIds.has(q.id)) continue;
    if (!q.required) continue;
    totalRequired += 1;
    const reason = isQuestionComplete(q, subByQ.get(q.id));
    if (reason === null) {
      completedRequired += 1;
    } else {
      missing.push({ question_id: q.id, reason });
    }
  }

  const progress =
    totalRequired === 0 ? 100 : Math.round((completedRequired * 100) / totalRequired);
  return { progress, totalRequired, completedRequired, visibleIds, missing };
}
